/**
 * The rules the mandate editor's form has to satisfy before its body is worth sending.
 *
 * The editor holds what the user entered, mistakes included, so that it can explain each one beside
 * its field instead of swallowing the input. That means the form can describe a mandate
 * `POST /mandates/preview` and the write routes refuse outright (`MandateWriteRequest` and its listing
 * policies in `app/forwards2/mandate_listing.py`), which is what these rules decide: the editor
 * reads them for its messages and the store reads them to hold the preview and the save back.
 */

import type { ListingWrite, ProductionRange } from "@/forwards2/forwardsTypes";

/** `constants.MANDATE_NAME_MAX`, which `MandateWriteRequest` applies to the name after trimming it. */
export const MANDATE_NAME_MAX = 128;

/** `constants.MANDATE_DESCRIPTION_MAX`, the longest description `MandateWriteRequest` accepts. */
export const MANDATE_DESCRIPTION_MAX = 2000;

/** Why a listing row cannot be sent: another row states the same period. */
export type ListingRowError = "duplicate";

/**
 * What is wrong with each listing row, by row index.
 *
 * Both rows of a repeated period are named rather than only the later one, since either of them can
 * be the one to move. The period picker offers no period another row holds, so only a mandate saved
 * with a repeat can show one.
 */
export function listingRowErrors(
  listings: readonly ListingWrite[]
): Map<number, ListingRowError> {
  const errors = new Map<number, ListingRowError>();
  const rowsByPeriod = new Map<string, number[]>();

  listings.forEach((listing, index) => {
    const period = `${listing.period_start}/${listing.period_end}`;
    rowsByPeriod.set(period, [...(rowsByPeriod.get(period) ?? []), index]);
  });

  for (const rows of rowsByPeriod.values()) {
    if (rows.length === 1) continue;
    for (const index of rows) errors.set(index, "duplicate");
  }
  return errors;
}

/** Why a production range cannot be sent: its last month is before its first. */
export type ProductionRangeError = "reversed";

/** Either end of the range may be open, so only a last month before the first is refused. */
export function productionRangeError(
  range: ProductionRange
): ProductionRangeError | null {
  const { production_from: from, production_until: until } = range;
  if (from !== null && until !== null && until < from) return "reversed";
  return null;
}

/** The decimal places `MandateWriteRequest.minimum_price` accepts, the scale of its `Numeric(13, 3)` column. */
export const MINIMUM_PRICE_DECIMALS = 3;

/** Why a minimum price cannot be sent: below zero, or finer than the column stores. */
export type MinimumPriceError = "negative" | "too_precise";

/**
 * What is wrong with the minimum price as the form holds it, a decimal string or null for none.
 *
 * The decimals are counted on the string, since a float multiplied up misjudges values such as
 * 1.005; a tiny number written in exponent form counts the places its exponent adds.
 */
export function minimumPriceError(
  value: string | null
): MinimumPriceError | null {
  if (value === null || value.trim() === "") return null;
  const price = Number(value);
  if (Number.isNaN(price)) return null;
  if (price < 0) return "negative";
  return decimalPlaces(value) > MINIMUM_PRICE_DECIMALS ? "too_precise" : null;
}

function decimalPlaces(value: string): number {
  const [mantissa, exponent = "0"] = value.trim().toLowerCase().split("e");
  const fraction = mantissa.split(".")[1] ?? "";
  return Math.max(0, fraction.replace(/0+$/, "").length - Number(exponent));
}
